import {
  getCurrencyInputStep,
  toSentenceCase,
} from '@omnipresentgroup/helpers';
import {
  InvoicingCurrencyEnum,
  InvoicingFrequencyEnum,
} from '@omnipresentgroup/types';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { useEffect, useMemo, useState } from 'react';

import type { EditContractingDetailsFormFields } from '../ContractorContractingDetailsEditModal';

const getArraySchemaFromEnum = (
  enumObject: object,
  transformTitle = true,
): JSONSchema7Definition[] =>
  Object.values(enumObject).map((type) => ({
    const: type,
    title: transformTitle ? toSentenceCase(type) : type,
  }));

export const useEditContractingDetailsSchema = ({
  formValues,
}: {
  formValues?: EditContractingDetailsFormFields;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});

  const startDate = useMemo(
    () => formValues?.dateRange?.startDate,
    [formValues?.dateRange?.startDate],
  );

  const endDate = useMemo(
    () => formValues?.dateRange?.endDate,
    [formValues?.dateRange?.endDate],
  );

  const currency = useMemo(
    () => formValues?.invoicingAmount?.currency,
    [formValues?.invoicingAmount?.currency],
  );

  useEffect(() => {
    setSchema({
      type: 'object',
      required: ['invoicingAmount'],
      properties: {
        invoicingAmount: {
          type: 'object',
          required: ['currency'],
          properties: {
            currency: {
              type: 'string',
              title: 'Currency',
              oneOf: getArraySchemaFromEnum(InvoicingCurrencyEnum, false),
            },
            amount: {
              type: 'number',
              title: 'Amount',
              minimum: 0.01,
            },
          },
        },
        frequency: {
          title: 'Frequency',
          type: 'string',
          oneOf: getArraySchemaFromEnum(InvoicingFrequencyEnum),
        },
        dateRange: {
          type: 'object',
          properties: {
            startDate: {
              title: 'Start date',
              type: 'string',
              format: 'date',
            },
            endDate: {
              title: 'End date',
              type: 'string',
              format: 'date',
            },
          },
        },
      },
    });

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      'ui:order': ['invoicingAmount', 'frequency', 'dateRange'],
      invoicingAmount: {
        'ui:label': false,
        'ui:columns': { columns: 12, gap: '16' },
        currency: {
          'ui:span': 4,
          'ui:placeholder': 'Select',
          'ui:tooltip':
            'This is the currency in which your contractor will issue invocies, and receive payment in their bank account.',
          'ui:readonly': true,
        },
        amount: {
          'ui:span': 8,
          'ui:placeholder': '0.00',
          'ui:options': {
            step: getCurrencyInputStep(currency),
          },
          'ui:tooltip':
            'This is the agreed payment between you and the contractor, and is only intended to help you keep track of your agreement in the platform. This may or may not be different to the amount/frequency your contractor invoices you.',
        },
      },
      frequency: {
        'ui:placeholder': 'Select',
      },
      dateRange: {
        'ui:order': ['startDate', 'endDate'],
        'ui:label': false,
        'ui:columns': { columns: 12, gap: '16' },
        startDate: {
          'ui:span': 6,
          'ui:options': {
            selectsStart: true,
            startDate: startDate,
            endDate: endDate,
            maxDate: endDate,
          },
        },
        endDate: {
          'ui:span': 6,
          'ui:options': {
            selectsEnd: true,
            startDate: startDate,
            endDate: endDate,
            minDate: startDate,
          },
        },
      },
    });
  }, [currency, endDate, startDate]);

  return {
    schema,
    uiSchema,
  };
};
